import React, {useState, useEffect} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Alert,
    CircularProgress,
    Chip,
    Tooltip,
    IconButton,
    useTheme,
} from '@mui/material';
import {
    CheckBox as CheckBoxIcon,
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
    IndeterminateCheckBox as IndeterminateCheckBoxIcon,
    Info as InfoIcon,
    GridView as MatrixIcon,
} from '@mui/icons-material';

const DocumentTopicMatrix = ({open, onClose, onSave, courseId, apiClient}) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [matrixData, setMatrixData] = useState(null);
    const [associations, setAssociations] = useState(new Map()); // Map<documentId, Set<topicId>>
    const [hasChanges, setHasChanges] = useState(false);

    const fetchMatrixData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.get(`/api/documents/matrix/${courseId}`);
            setMatrixData(response);

            // Initialize associations map from current state
            const initialAssociations = new Map();
            response.documents.forEach(doc => {
                initialAssociations.set(doc.document_id, new Set(doc.current_topic_ids || []));
            });
            setAssociations(initialAssociations);
            setHasChanges(false);
        } catch (err) {
            console.error('Error fetching matrix data:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to load topic associations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && courseId && apiClient) {
            fetchMatrixData();
        }
    }, [open, courseId, apiClient]);

    const handleTopicToggle = (documentId, topicId) => {
        setAssociations(prev => {
            const newAssociations = new Map(prev);
            const currentTopics = new Set(newAssociations.get(documentId) || []);

            if (currentTopics.has(topicId)) {
                currentTopics.delete(topicId);
            } else {
                currentTopics.add(topicId);
            }

            newAssociations.set(documentId, currentTopics);
            return newAssociations;
        });
        setHasChanges(true);
    };

    const handleAvailableToAllToggle = (documentId) => {
        setAssociations(prev => {
            const newAssociations = new Map(prev);
            const currentTopics = newAssociations.get(documentId) || new Set();

            if (currentTopics.size === 0) {
                // Currently available to all - clicking should do nothing since it's already the state
                return prev;
            } else {
                // Has specific topics, clear them to make available to all
                newAssociations.set(documentId, new Set());
                return newAssociations;
            }
        });
        setHasChanges(true);
    };

    const handleSave = async () => {
        if (!matrixData || !hasChanges) {
            onSave();
            return;
        }

        setSaving(true);
        setError(null);

        try {
            // Convert associations to API format
            const associationsArray = Array.from(associations.entries()).map(([documentId, topicIds]) => ({
                document_id: documentId,
                topic_ids: Array.from(topicIds)
            }));

            const requestData = {
                course_id: courseId,
                associations: associationsArray
            };

            await apiClient.post('/api/documents/bulk-associate-topics', requestData);

            setHasChanges(false);
            onSave(); // This will refresh the parent and close the dialog
        } catch (err) {
            console.error('Error saving associations:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to save topic associations');
        } finally {
            setSaving(false);
        }
    };

    const getColumnHeaderState = (topicId) => {
        if (!matrixData) return 'none';

        const documentsWithThisTopic = matrixData.documents.filter(doc => {
            const docTopics = associations.get(doc.document_id) || new Set();
            return docTopics.has(topicId);
        });

        if (documentsWithThisTopic.length === 0) return 'none';
        if (documentsWithThisTopic.length === matrixData.documents.length) return 'all';
        return 'some';
    };

    const getAvailableToAllHeaderState = () => {
        if (!matrixData) return 'none';

        const documentsAvailableToAll = matrixData.documents.filter(doc => {
            const docTopics = associations.get(doc.document_id) || new Set();
            return docTopics.size === 0;
        });

        if (documentsAvailableToAll.length === 0) return 'none';
        if (documentsAvailableToAll.length === matrixData.documents.length) return 'all';
        return 'some';
    };

    const handleColumnHeaderClick = (topicId) => {
        const state = getColumnHeaderState(topicId);
        const newAssociations = new Map(associations);

        if (state === 'all') {
            // Uncheck all for this topic
            matrixData.documents.forEach(doc => {
                const currentTopics = new Set(newAssociations.get(doc.document_id) || []);
                currentTopics.delete(topicId);
                newAssociations.set(doc.document_id, currentTopics);
            });
        } else {
            // Check all for this topic
            matrixData.documents.forEach(doc => {
                const currentTopics = new Set(newAssociations.get(doc.document_id) || []);
                currentTopics.add(topicId);
                newAssociations.set(doc.document_id, currentTopics);
            });
        }

        setAssociations(newAssociations);
        setHasChanges(true);
    };

    const handleAvailableToAllHeaderClick = () => {
        const state = getAvailableToAllHeaderState();
        const newAssociations = new Map(associations);

        if (state === 'all') {
            // Currently all are available to all, do nothing
            return;
        } else {
            // Make all available to all (clear all topic assignments)
            matrixData.documents.forEach(doc => {
                newAssociations.set(doc.document_id, new Set());
            });
        }

        setAssociations(newAssociations);
        setHasChanges(true);
    };

    const renderColumnHeaderCheckbox = (topicId) => {
        const state = getColumnHeaderState(topicId);

        if (state === 'all') {
            return <CheckBoxIcon color="primary"/>;
        } else if (state === 'some') {
            return <IndeterminateCheckBoxIcon color="primary"/>;
        } else {
            return <CheckBoxOutlineBlankIcon/>;
        }
    };

    const renderAvailableToAllHeaderCheckbox = () => {
        const state = getAvailableToAllHeaderState();

        if (state === 'all') {
            return <CheckBoxIcon color="primary"/>;
        } else if (state === 'some') {
            return <IndeterminateCheckBoxIcon color="primary"/>;
        } else {
            return <CheckBoxOutlineBlankIcon/>;
        }
    };

    if (!open) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {minHeight: '50vh'}
            }}
        >
            <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1, px: 3}}>
                <MatrixIcon/>
                Document-Topic Associations
                <Tooltip title="Assign documents to multiple topics, or leave them available to all topics by default">
                    <IconButton size="small">
                        <InfoIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
            </DialogTitle>

            <DialogContent sx={{p: 0}}>
                {loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300}}>
                        <CircularProgress/>
                    </Box>
                ) : error ? (
                    <Box sx={{p: 3}}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                ) : matrixData ? (
                    <Box sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                        {/* Summary Info - No background, consistent padding */}
                        <Box sx={{
                            px: 3,
                            py: 2,
                            borderBottom: 1,
                            borderColor: 'divider'
                        }}>
                            <Typography variant="h6" gutterBottom>
                                {matrixData.course_title}
                            </Typography>
                            <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                                <Chip
                                    label={`${matrixData.matrix_summary.total_documents} Documents`}
                                    variant="outlined"
                                    size="small"
                                />
                                <Chip
                                    label={`${matrixData.matrix_summary.total_topics} Topics`}
                                    variant="outlined"
                                    size="small"
                                />
                                <Chip
                                    label={`${matrixData.matrix_summary.available_to_all_documents} Available to All`}
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                />
                            </Box>
                        </Box>

                        {/* Matrix Table - Clean, no backgrounds */}
                        <TableContainer sx={{flexGrow: 1, maxHeight: 'calc(100vh - 300px)'}}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{minWidth: 200, fontWeight: 'bold'}}>
                                            Document
                                        </TableCell>

                                        {/* Available to All Topics Column */}
                                        <TableCell align="center" sx={{minWidth: 120}}>
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}>
                                                <Tooltip title="Toggle all documents to be available to all topics">
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleAvailableToAllHeaderClick}
                                                    >
                                                        {renderAvailableToAllHeaderCheckbox()}
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography variant="caption"
                                                            sx={{fontWeight: 'bold', textAlign: 'center'}}>
                                                    Available to All
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    (Default)
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        {/* Topic Columns */}
                                        {matrixData.topics.map(topic => (
                                            <TableCell key={topic.topic_id} align="center" sx={{minWidth: 120}}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 0.5
                                                }}>
                                                    <Tooltip title={`Toggle all documents for ${topic.topic_name}`}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleColumnHeaderClick(topic.topic_id)}
                                                        >
                                                            {renderColumnHeaderCheckbox(topic.topic_id)}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Typography variant="caption"
                                                                sx={{fontWeight: 'bold', textAlign: 'center'}}>
                                                        {topic.topic_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        ({topic.document_count} docs)
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {matrixData.documents.map(document => {
                                        const docTopics = associations.get(document.document_id) || new Set();
                                        const isAvailableToAll = docTopics.size === 0;

                                        return (
                                            <TableRow key={document.document_id}>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" sx={{fontWeight: 'medium'}}>
                                                            {document.document_name}
                                                        </Typography>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            mt: 0.5
                                                        }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {document.file_type} • {document.file_size_mb} MB
                                                            </Typography>
                                                            {document.is_public && (
                                                                <Chip
                                                                    label="Public"
                                                                    size="small"
                                                                    color="success"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                            {docTopics.size > 0 && (
                                                                <Chip
                                                                    label={`${docTopics.size} topic${docTopics.size > 1 ? 's' : ''}`}
                                                                    size="small"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>

                                                {/* Available to All Column */}
                                                <TableCell align="center">
                                                    <Checkbox
                                                        checked={isAvailableToAll}
                                                        onChange={() => handleAvailableToAllToggle(document.document_id)}
                                                        color="primary"
                                                    />
                                                </TableCell>

                                                {/* Topic Columns */}
                                                {matrixData.topics.map(topic => (
                                                    <TableCell key={topic.topic_id} align="center">
                                                        <Checkbox
                                                            checked={docTopics.has(topic.topic_id)}
                                                            onChange={() => handleTopicToggle(document.document_id, topic.topic_id)}
                                                            color="primary"
                                                        />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                ) : null}
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || !hasChanges}
                    startIcon={saving ? <CircularProgress size={20}/> : null}
                >
                    {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DocumentTopicMatrix;